import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** Indonesian mirror of the spouse-visa checklist guide. */
const TITLE = "Dokumen visa pasangan dari Thailand — daftar periksa dan urutan pengesahan";
const DESC =
  "Dokumen Thailand yang dibutuhkan untuk visa pasangan, urutan penerjemahan dan pengesahan, serta kesalahan yang paling sering menyebabkan penolakan.";
const PATH = "/blog/spouse-visa-document-checklist";
const URL = `/id${PATH}`;
const PUBLISHED = "2026-08-10";

const FAQ = [
  {
    question: "Dokumen apa saja yang biasanya diminta?",
    answer:
      "Akta perkawinan, sertifikat status perkawinan, akta kelahiran, KK, KTP dan paspor, serta bukti hubungan seperti foto, riwayat komunikasi, dan bukti tempat tinggal bersama.",
  },
  {
    question: "Apakah semua dokumen harus disahkan?",
    answer:
      "Tidak selalu. Dokumen sipil biasanya perlu terjemahan dan pengesahan, sedangkan bukti hubungan umumnya cukup dilampirkan dalam bahasa yang diminta.",
  },
  {
    question: "Bagaimana urutan yang benar?",
    answer:
      "Ambil salinan baru dari kantor catatan sipil, notarisasi untuk dokumen yang dibuat sendiri, pengesahan Departemen Konsuler, penerjemahan, lalu legalisasi kedutaan bila diminta.",
  },
  {
    question: "Berapa lama masa berlaku salinan dokumen?",
    answer:
      "Banyak kedutaan hanya menerima salinan yang diterbitkan dalam 3–6 bulan terakhir. Sesuaikan waktu pengambilan dengan jadwal pengajuan.",
  },
  {
    question: "Perlu terjemahan bahasa apa?",
    answer:
      "Umumnya bahasa Inggris atau bahasa resmi negara tujuan. Sebagian negara mewajibkan penerjemah yang diakui, misalnya NAATI untuk Australia.",
  },
  {
    question: "Bagaimana jika nama pernah berubah?",
    answer:
      "Lampirkan sertifikat perubahan nama dan pastikan seluruh dokumen serta terjemahan memakai ejaan yang konsisten dengan paspor.",
  },
  {
    question: "Kenapa pengajuan visa pasangan ditolak?",
    answer:
      "Penyebab tersering adalah bukti hubungan yang tipis, salinan dokumen kedaluwarsa, urutan pengesahan salah, dan ketidaksesuaian nama antar dokumen.",
  },
];

export const Route = createFileRoute("/id/blog/spouse-visa-document-checklist")({
  component: IdSpouseVisaGuide,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { property: "og:locale", content: "id" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("spouse-visa-document-checklist") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("spouse-visa-document-checklist", "id") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("spouse-visa-document-checklist") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "visa pasangan Thailand, dokumen visa suami istri, akta perkawinan Thailand, pengesahan dokumen visa",
        },
      ],
      links: canonicalAndHreflang(PATH, null, "id"),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "Beranda", url: "/id" },
              { name: "Panduan", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("spouse-visa-document-checklist"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Visa & Immigration",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const DOCS: Array<{ d: string; note: string }> = [
  { d: "Akta perkawinan dan sertifikat status perkawinan", note: "Ambil salinan baru dari kantor catatan sipil" },
  { d: "Akta kelahiran dan kartu keluarga", note: "Untuk memastikan identitas dan hubungan keluarga" },
  { d: "KTP dan paspor", note: "Ejaan nama harus konsisten dengan seluruh dokumen" },
  { d: "Surat keterangan catatan kepolisian", note: "Diminta banyak kedutaan, siapkan lebih awal" },
  { d: "Bukti keuangan dan tempat tinggal", note: "Rekening, surat kerja, atau bukti sponsor sesuai permintaan" },
  { d: "Bukti hubungan", note: "Foto, riwayat komunikasi, tiket perjalanan, bukti tinggal bersama" },
];

const ORDER: string[] = [
  "Unduh daftar dokumen resmi dari kedutaan atau instansi imigrasi tujuan",
  "Ambil salinan baru dokumen sipil dari kantor catatan sipil",
  "Notarisasi dokumen yang dibuat sendiri, misalnya pernyataan atau surat kuasa",
  "Pengesahan di Departemen Konsuler Kementerian Luar Negeri Thailand",
  "Penerjemahan lengkap termasuk cap, stempel, dan catatan pengesahan",
  "Legalisasi kedutaan bila diminta, lalu susun berkas sesuai urutan daftar resmi",
];

const MISTAKES: string[] = [
  "Bukti hubungan terlalu sedikit atau tidak berurutan waktu",
  "Salinan dokumen sipil sudah melewati batas waktu penerimaan",
  "Menerjemahkan sebelum pengesahan selesai",
  "Ejaan nama berbeda antara paspor, akta, dan terjemahan",
  "Format terjemahan tidak sesuai permintaan, misalnya bukan NAATI untuk Australia",
  "Berkas tidak disusun sesuai urutan daftar resmi sehingga sulit diperiksa",
];

function IdSpouseVisaGuide() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/id" className="text-muted-foreground hover:text-foreground">
          Beranda
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:text-foreground">
          Panduan
        </Link>
      </nav>

      <h1>{TITLE}</h1>
      <BlogHero slug="spouse-visa-document-checklist" locale="id" />
      <p className="answer-summary speakable-summary">
        Pengajuan visa pasangan jarang gagal karena satu dokumen hilang, melainkan karena urutan pengesahan yang salah dan
        bukti hubungan yang tipis. Ambil salinan baru dokumen sipil, notarisasi dokumen buatan sendiri, sahkan di
        Departemen Konsuler, baru terjemahkan dan legalisasi di kedutaan bila diminta.
      </p>

      <h2>1. Daftar dokumen</h2>
      <ul>
        {DOCS.map((x) => (
          <li key={x.d}>
            <strong>{x.d}:</strong> {x.note}
          </li>
        ))}
      </ul>

      <h2>2. Urutan langkah</h2>
      <ol>
        {ORDER.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>

      <h2>3. Penyebab penolakan</h2>
      <ul>
        {MISTAKES.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>

      <h2>4. Pertanyaan yang sering diajukan</h2>
      <dl>
        {FAQ.map((f) => (
          <div key={f.question}>
            <dt>
              <strong>{f.question}</strong>
            </dt>
            <dd>{f.answer}</dd>
          </div>
        ))}
      </dl>

      <h2>5. Rujukan dan layanan terkait</h2>
      <p className="text-sm">
        Rujukan:{" "}
        <a href="https://www.bora.dopa.go.th/" target="_blank" rel="noreferrer">
          Kantor Administrasi Pendaftaran (BORA), Kementerian Dalam Negeri Thailand
        </a>{" "}
        ·{" "}
        <a href="https://consular.mfa.go.th/en/publicservice/legalization" target="_blank" rel="noreferrer">
          Departemen Konsuler, Kementerian Luar Negeri Thailand
        </a>
      </p>
      <ul className="not-prose mt-4 grid gap-2 text-sm">
        <li>
          <Link to="/id/visa" className="text-primary underline">
            Layanan visa dan imigrasi
          </Link>
        </li>
        <li>
          <Link to="/id/marriage" className="text-primary underline">
            Pernikahan dan dokumen status perkawinan
          </Link>
        </li>
        <li>
          <Link to="/id/pcc" className="text-primary underline">
            Surat keterangan catatan kepolisian
          </Link>
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        Ditinjau pada {PUBLISHED}. Informasi ini bersifat umum, bukan nasihat hukum untuk kasus tertentu. Jika Anda tidak
        ingin mengurus setiap langkah sendiri, tim kami dengan pengalaman lebih dari 15 tahun dapat menjadi konsultan
        sekaligus menyiapkan seluruh berkas — hubungi kami lewat telepon, LINE, atau email.
      </p>
    </article>
  );
}
